import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { useNavigate } from 'react-router-dom';
import { getTeamReadiness, GetTeamReadinessOutputType } from 'zite-endpoints-sdk';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Users, Download, Shield, CalendarDays, CheckSquare, AlertTriangle } from 'lucide-react';
import { isManagerPlus } from '@/lib/roles';

type TeamData = GetTeamReadinessOutputType;
type Member = TeamData['teamMembers'][0];

export default function TeamReadiness() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<TeamData | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!user) return;
    if (!isManagerPlus(user.role)) { navigate('/'); return; }
    getTeamReadiness({}).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [user, navigate]);

  const exportCsv = () => {
    if (!data) return;
    const headers = ['Name', 'Role', 'Department', 'Training Done', 'Training Total', 'Compliance', 'Pending Leave', 'Active Tasks', 'Onboarding'];
    const rows = data.teamMembers.map(m => [m.name, m.role, m.department, m.trainingCompleted, m.trainingTotal, m.complianceStatus, m.pendingLeave, m.activeTaskCount, m.onboardingStatus]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'team-readiness.csv'; a.click();
  };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24 w-full" />)}</div>;
  if (!data) return null;

  const filtered = data.teamMembers.filter(m => !search || m.name.toLowerCase().includes(search.toLowerCase()) || m.department.toLowerCase().includes(search.toLowerCase()));
  const { summary } = data;

  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-xl font-bold">Team Readiness</h1>
            <p className="text-sm text-muted-foreground">Safe team overview — no sensitive HR data</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv}><Download className="w-4 h-4 mr-1.5" />Export CSV</Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <SummaryCard icon={<Users className="w-4 h-4" />} label="Staff" value={summary.totalStaff} />
        <SummaryCard icon={<CheckSquare className="w-4 h-4 text-green-500" />} label="Training OK" value={summary.trainingComplete} />
        <SummaryCard icon={<AlertTriangle className="w-4 h-4 text-red-500" />} label="Compliance Gaps" value={summary.complianceGaps} />
        <SummaryCard icon={<CalendarDays className="w-4 h-4 text-blue-500" />} label="Pending Leave" value={summary.pendingLeave} />
        <SummaryCard icon={<Shield className="w-4 h-4 text-amber-500" />} label="Pending Requests" value={summary.pendingRequests} />
        <SummaryCard icon={<Users className="w-4 h-4 text-primary" />} label="New Starters" value={summary.newStarters} />
      </div>

      <Input placeholder="Search team members..." value={search} onChange={e => setSearch(e.target.value)} className="max-w-md" />

      <div className="space-y-3">
        {filtered.map(m => {
          const pct = m.trainingTotal > 0 ? Math.round((m.trainingCompleted / m.trainingTotal) * 100) : 0;
          return (
            <Card key={m.id}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium">{m.name}</p>
                      <Badge variant="secondary" className="text-xs">{m.role}</Badge>
                      {m.department && <span className="text-xs text-muted-foreground">{m.department}</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Progress value={pct} className="h-1.5 flex-1 max-w-32" />
                      <span className="text-xs text-muted-foreground">{m.trainingCompleted}/{m.trainingTotal}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap shrink-0">
                    <Badge variant={m.complianceStatus === 'OK' ? 'default' : 'destructive'} className="text-xs">
                      {m.complianceStatus === 'OK' ? '✓ Compliant' : '⚠ Gaps'}
                    </Badge>
                    {m.pendingLeave > 0 && <Badge variant="secondary" className="text-xs">📅 {m.pendingLeave} leave</Badge>}
                    {m.activeTaskCount > 0 && <Badge variant="outline" className="text-xs">📋 {m.activeTaskCount} tasks</Badge>}
                    {m.onboardingStatus !== 'Approved' && m.onboardingStatus !== 'Not Started' && (
                      <Badge variant="secondary" className="text-xs">🆕 {m.onboardingStatus}</Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function SummaryCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <Card>
      <CardContent className="py-3 text-center">
        <div className="flex justify-center mb-1">{icon}</div>
        <p className="text-xl font-bold">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}
